import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './PhotoGallery.css';
import { API_BASE, mediaSrc } from '../utils/api';

const PAGE_SIZE = 9;

/**
 * Dernek galerisi.
 *
 * Fotoğraflar 9'arlı sayfalarla, en son yüklenen en başta olacak şekilde sunucudan
 * çekilir (bkz. GET /api/gallery?page&limit): tüm galeri tek seferde inmediği için
 * ilk açılış hafif kalıyor. Sonraki sayfa "Daha Fazla" butonuyla eklenir — hem
 * anasayfada hem Derneğimiz sayfasında aynı.
 */
const PhotoGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [moreError, setMoreError] = useState('');
  const [total, setTotal] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  // Aynı sayfayı iki kez istemeyi engeller (hızlı arka arkaya tıklama).
  const requestedPages = useRef(new Set());

  const loadPage = useCallback((pageNo) => {
    if (requestedPages.current.has(pageNo)) return;
    requestedPages.current.add(pageNo);
    setLoading(true);
    setMoreError('');

    // İlk sayfa hatası bölümün yerine mesaj gösterir; sonraki sayfalarda ise
    // yüklenmiş fotoğraflar kalır, butonun altında tekrar denenebilir uyarı çıkar.
    const fail = (msg) => {
      requestedPages.current.delete(pageNo);
      if (pageNo === 1) setError(msg);
      else setMoreError('Fotoğraflar yüklenemedi. Lütfen tekrar deneyin.');
    };

    fetch(`${API_BASE}/api/gallery?page=${pageNo}&limit=${PAGE_SIZE}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!data.success) {
          fail('Fotoğraflar yüklenemedi.');
          return;
        }
        setPhotos((prev) => {
          // Aynı id iki sayfada gelirse (arada yeni kayıt eklenirse) tekrar etmesin.
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...(data.items || []).filter((p) => !seen.has(p.id))];
        });
        setHasMore(Boolean(data.hasMore));
        setTotal(Number(data.total) || 0);
        setPage(pageNo);
      })
      .catch(() => fail('Fotoğraflar şu an yüklenemiyor. Lütfen daha sonra tekrar deneyin.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    loadPage(page + 1);
  }, [loading, hasMore, page, loadPage]);

  const openModal = (photo) => {
    const index = photos.findIndex((p) => p.id === photo.id);
    setCurrentIndex(index);
    setSelectedPhoto(photo);
  };

  const closeModal = () => setSelectedPhoto(null);

  // Modal açıkken arka sayfanın kaymasını engelle (sayfayı <html> kaydırdığı için ikisi de kilitlenir)
  useEffect(() => {
    if (!selectedPhoto) return;
    const root = document.documentElement;
    const prevRoot = root.style.overflow;
    const prevBody = document.body.style.overflow;
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      root.style.overflow = prevRoot;
      document.body.style.overflow = prevBody;
    };
  }, [selectedPhoto]);

  useEffect(() => {
    if (!selectedPhoto) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedPhoto(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedPhoto]);

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedPhoto(photos[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === photos.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedPhoto(photos[newIndex]);
  };

  if (photos.length === 0 && !error && !loading) return null;

  return (
    <section className="photo-gallery-section">
      <div className="photo-gallery-container">
        <div className="photo-gallery-header">
          <div className="photo-header-content">
            <h2 className="photo-gallery-title">
              DERNEĞİMİZDEN
              <span className="photo-title-accent"> FOTOĞRAFLAR</span>
            </h2>
            <p className="photo-gallery-subtitle">
              İskoçya'da birlikte yaşadığımız güzel anılar
            </p>
          </div>
          <div className="photo-header-decoration">
            <div className="photo-decoration-line"></div>
            <div className="photo-decoration-circle">
              <span className="photo-decoration-icon">📸</span>
            </div>
            <div className="photo-decoration-line"></div>
          </div>
        </div>

        {error ? (
          <p className="text-center text-gray-400 text-sm py-8">{error}</p>
        ) : (
          <>
            <div className="photo-grid">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`photo-item photo-item-${index + 1}`}
                  onClick={() => openModal(photo)}
                >
                  <div className="photo-wrapper">
                    <img
                      src={mediaSrc(photo.src)}
                      alt={photo.alt}
                      className="photo-image"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="photo-overlay">
                      <div className="photo-overlay-content">
                        <h3 className="photo-overlay-title">{photo.title}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="photo-info">
                    <h3 className="photo-info-title">{photo.title}</h3>
                    <p className="photo-info-description">{photo.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="photo-load-more">
                <button
                  type="button"
                  className="photo-load-more-btn"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? 'Yükleniyor…' : moreError ? 'Tekrar Dene' : 'Daha Fazla'}
                </button>
                <span className="photo-load-more-count">
                  {photos.length} / {total} fotoğraf
                </span>
                {moreError && <span className="photo-load-more-error">{moreError}</span>}
              </div>
            )}
          </>
        )}

        {/* Lightbox body'ye portal ile taşınır: aksi halde main (z-index:1) ve
            .photo-gallery-container (z-index:2) yığın bağlamlarına hapsolup
            alttaki bölümlerin altında kalıyor (özellikle mobil tarayıcılarda). */}
        {selectedPhoto && createPortal(
          <div className="photo-modal" onClick={closeModal}>
            <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="photo-modal-close" onClick={closeModal}>✕</button>
              <button className="photo-modal-prev" onClick={goToPrevious}>‹</button>
              <button className="photo-modal-next" onClick={goToNext}>›</button>
              <img src={mediaSrc(selectedPhoto.src)} alt={selectedPhoto.alt} className="photo-modal-image" />
              <div className="photo-modal-info">
                <h3 className="photo-modal-title">{selectedPhoto.title}</h3>
                <p className="photo-modal-description">{selectedPhoto.description}</p>
                <p className="photo-modal-counter">{currentIndex + 1} / {photos.length}</p>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </section>
  );
};

export default PhotoGallery;
